import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Download,
  Trash2,
  MoreHorizontal,
  RefreshCw,
  Tent,
  Users,
  Euro,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  Eye,
  Upload,
  FileText,
  Copy,
  ArrowLeft,
  X,
} from "lucide-react";
import api from "../lib/api";

// Helper function to format date as dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  // Handle yyyy-mm-dd format
  if (dateStr.includes("-")) {
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  }
  return dateStr;
};

const STATUS_CONFIG = {
  pending: { label: "In Attesa", icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10" },
  confirmed: { label: "Confermato", icon: CheckCircle, color: "text-green-500", bg: "bg-green-500/10" },
  cancelled: { label: "Annullato", icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
};

const PAYMENT_CONFIG = {
  pending: { label: "Da Pagare", color: "text-orange-500", bg: "bg-orange-500/10" },
  partial: { label: "Parziale", color: "text-blue-500", bg: "bg-blue-500/10" },
  paid: { label: "Pagato", color: "text-green-500", bg: "bg-green-500/10" },
};

// Registration Detail Page Component
function RegistrationDetail({ registrationId, onBack }) {
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchRegistration();
  }, [registrationId]);

  const fetchRegistration = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/sendme-camp/registrations/${registrationId}`);
      setRegistration(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
      onBack();
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/sendme-camp/registrations/${registrationId}/status?status=${newStatus}`);
      toast.success("Stato aggiornato");
      fetchRegistration();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handlePaymentChange = async (newPaymentStatus) => {
    try {
      await api.put(`/sendme-camp/registrations/${registrationId}/payment?payment_status=${newPaymentStatus}`);
      toast.success("Stato pagamento aggiornato");
      fetchRegistration();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleUploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post(`/sendme-camp/registrations/${registrationId}/receipts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ricevuta caricata");
      fetchRegistration();
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteReceipt = async (filename) => {
    if (!window.confirm("Eliminare questa ricevuta?")) return;
    try {
      await api.delete(`/sendme-camp/registrations/${registrationId}/receipts/${filename}`);
      toast.success("Ricevuta eliminata");
      fetchRegistration();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDownloadReceipt = async (filename) => {
    try {
      const response = await api.get(`/sendme-camp/receipts/${filename}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Errore nel download");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato!");
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
      </div>
    );
  }

  if (!registration) return null;

  const statusConfig = STATUS_CONFIG[registration.status] || STATUS_CONFIG.pending;
  const paymentConfig = PAYMENT_CONFIG[registration.payment_status] || PAYMENT_CONFIG.pending;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla lista
        </Button>
      </div>

      {/* Registration Code Banner */}
      <Card className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Codice Iscrizione</p>
              <div className="flex items-center gap-2">
                <span className="text-4xl font-mono font-bold text-yellow-500">
                  {registration.registration_code}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(registration.registration_code)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <Select value={registration.status} onValueChange={handleStatusChange}>
                <SelectTrigger className={`w-36 ${statusConfig.bg} ${statusConfig.color} border-0`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">In Attesa</SelectItem>
                  <SelectItem value="confirmed">Confermato</SelectItem>
                  <SelectItem value="cancelled">Annullato</SelectItem>
                </SelectContent>
              </Select>
              <Select value={registration.payment_status || "pending"} onValueChange={handlePaymentChange}>
                <SelectTrigger className={`w-36 ${paymentConfig.bg} ${paymentConfig.color} border-0`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Da Pagare</SelectItem>
                  <SelectItem value="partial">Parziale</SelectItem>
                  <SelectItem value="paid">Pagato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Data */}
        <Card>
          <CardHeader>
            <CardTitle>Dati Personali</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Nome</label>
                <p className="font-medium">{registration.name}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Cognome</label>
                <p className="font-medium">{registration.surname}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Data di Nascita</label>
                <p className="font-medium">{formatDate(registration.birth_date)}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Sesso</label>
                <p className="font-medium">{registration.gender === "M" ? "Maschio" : "Femmina"}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Telefono</label>
                <p className="font-medium">{registration.phone}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Email</label>
                <p className="font-medium">{registration.email}</p>
              </div>
              <div className="col-span-2">
                <label className="text-sm text-muted-foreground">Chiesa</label>
                <p className="font-medium">{registration.church}</p>
              </div>
              {registration.room_preferences && (
                <div className="col-span-2">
                  <label className="text-sm text-muted-foreground">Preferenze Stanza</label>
                  <p className="font-medium">{registration.room_preferences}</p>
                </div>
              )}
              {registration.notes && (
                <div className="col-span-2">
                  <label className="text-sm text-muted-foreground">Note</label>
                  <p className="font-medium">{registration.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Riepilogo Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Partecipante principale</span>
                <span className="font-medium">€190</span>
              </div>
              {registration.family_members?.map((fm, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">
                    {fm.name} {fm.surname}
                    {fm.is_child && ` (${fm.child_age_range})`}
                  </span>
                  <span className="font-medium">€{fm.price}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-4 border-t-2">
                <span className="font-medium">Acconto (€50 x {registration.total_participants} persone)</span>
                <span className="text-xl font-bold text-orange-500">€{registration.deposit_amount || registration.total_participants * 50}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-lg font-bold">Totale</span>
                <span className="text-2xl font-bold text-green-500">€{registration.total_price}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Family Members */}
      {registration.family_members?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Familiari ({registration.family_members.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {registration.family_members.map((fm, idx) => (
                <div key={idx} className="bg-muted/50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">{fm.name} {fm.surname}</span>
                    <span className="text-sm text-muted-foreground">€{fm.price}</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Data nascita: {formatDate(fm.birth_date)}</p>
                    <p>Sesso: {fm.gender === "M" ? "Maschio" : "Femmina"}</p>
                    {fm.is_child && <p className="text-yellow-500">Bambino {fm.child_age_range}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Ricevute ({registration.receipts?.length || 0})
            </CardTitle>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUploadReceipt}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                size="sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Caricamento..." : "Carica Ricevuta"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {registration.receipts?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nessuna ricevuta caricata
            </p>
          ) : (
            <div className="space-y-2">
              {registration.receipts?.map((receipt, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between bg-muted/50 rounded-lg p-3"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">{receipt.original_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Caricato il {new Date(receipt.uploaded_at).toLocaleDateString("it-IT")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDownloadReceipt(receipt.filename)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-400"
                      onClick={() => handleDeleteReceipt(receipt.filename)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            <p>ID: {registration.id}</p>
            <p>Data iscrizione: {new Date(registration.created_at).toLocaleString("it-IT")}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// Main Admin Page Component
export default function SendMeCampAdminPage() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, revenue: 0, confirmed: 0, participants: 0, deposits: 0 });
  const [selectedRegistrationId, setSelectedRegistrationId] = useState(null);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await api.get("/sendme-camp/registrations");
      setRegistrations(response.data);
      
      const total = response.data.length;
      const revenue = response.data.reduce((sum, r) => sum + (r.total_price || 0), 0);
      const confirmed = response.data.filter(r => r.status === "confirmed").length;
      const participants = response.data.reduce((sum, r) => sum + (r.total_participants || 1), 0);
      const deposits = response.data.reduce((sum, r) => sum + (r.deposit_amount || (r.total_participants || 1) * 50), 0);
      setStats({ total, revenue, confirmed, participants, deposits });
    } catch (error) {
      toast.error("Errore nel caricamento delle iscrizioni");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (registrationId, newStatus) => {
    try {
      await api.put(`/sendme-camp/registrations/${registrationId}/status?status=${newStatus}`);
      toast.success("Stato aggiornato");
      fetchRegistrations();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const handlePaymentChange = async (registrationId, newPaymentStatus) => {
    try {
      await api.put(`/sendme-camp/registrations/${registrationId}/payment?payment_status=${newPaymentStatus}`);
      toast.success("Stato pagamento aggiornato");
      fetchRegistrations();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (registrationId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa iscrizione?")) return;
    try {
      await api.delete(`/sendme-camp/registrations/${registrationId}`);
      toast.success("Iscrizione eliminata");
      fetchRegistrations();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/sendme-camp/registrations/export", {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `iscrizioni_sendme_conference_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export completato");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Codice copiato!");
  };

  // Show detail view if a registration is selected
  if (selectedRegistrationId) {
    return (
      <RegistrationDetail
        registrationId={selectedRegistrationId}
        onBack={() => {
          setSelectedRegistrationId(null);
          fetchRegistrations();
        }}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">SendME Conference - Iscrizioni</h1>
          <p className="text-muted-foreground">23-29 Agosto 2026 • Centro Ghilgal, Mondragone (CE)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRegistrations}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" />
            Esporta Excel
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Iscrizioni</p>
                <p className="text-3xl font-bold text-yellow-500">{stats.total}</p>
              </div>
              <Tent className="w-10 h-10 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Partecipanti</p>
                <p className="text-3xl font-bold text-blue-500">{stats.participants}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Acconti Totali</p>
                <p className="text-3xl font-bold text-orange-500">€{stats.deposits}</p>
              </div>
              <CreditCard className="w-10 h-10 text-orange-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Incasso Previsto</p>
                <p className="text-3xl font-bold text-green-500">€{stats.revenue}</p>
              </div>
              <Euro className="w-10 h-10 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Confermate</p>
                <p className="text-3xl font-bold text-emerald-500">{stats.confirmed}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tent className="w-5 h-5" />
            Lista Iscrizioni
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
            </div>
          ) : registrations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Tent className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna iscrizione</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codice</TableHead>
                    <TableHead>Nominativo</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead>Chiesa</TableHead>
                    <TableHead className="text-center">N°</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registrations.map((reg) => {
                    const statusConfig = STATUS_CONFIG[reg.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    const paymentConfig = PAYMENT_CONFIG[reg.payment_status] || PAYMENT_CONFIG.pending;
                    
                    return (
                      <TableRow 
                        key={reg.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedRegistrationId(reg.id)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <span className="font-mono font-bold text-yellow-500">
                              {reg.registration_code || "-"}
                            </span>
                            {reg.registration_code && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => copyToClipboard(reg.registration_code)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{reg.name} {reg.surname}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(reg.created_at).toLocaleDateString("it-IT")}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{reg.phone}</div>
                          <div className="text-xs text-muted-foreground">{reg.email}</div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{reg.church}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-bold">{reg.total_participants || 1}</span>
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          €{reg.total_price}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={reg.status}
                            onValueChange={(value) => handleStatusChange(reg.id, value)}
                          >
                            <SelectTrigger className={`w-28 h-8 ${statusConfig.bg} ${statusConfig.color} border-0`}>
                              <div className="flex items-center gap-1">
                                <StatusIcon className="w-3 h-3" />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">In Attesa</SelectItem>
                              <SelectItem value="confirmed">Confermato</SelectItem>
                              <SelectItem value="cancelled">Annullato</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={reg.payment_status || "pending"}
                            onValueChange={(value) => handlePaymentChange(reg.id, value)}
                          >
                            <SelectTrigger className={`w-28 h-8 ${paymentConfig.bg} ${paymentConfig.color} border-0`}>
                              <div className="flex items-center gap-1">
                                <CreditCard className="w-3 h-3" />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Da Pagare</SelectItem>
                              <SelectItem value="partial">Parziale</SelectItem>
                              <SelectItem value="paid">Pagato</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setSelectedRegistrationId(reg.id)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Dettagli
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(reg.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
